import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  ApiQueryParams,
  CallGetResponse,
  CallListResponse,
  Call_V2,
} from 'src/api/generated';
import { V2CallsService } from 'src/api/generated';
import type { ApiResponse } from 'src/hooks/api/api';
import { useMemoizedArrayData } from 'src/hooks/api/api';
import { awaitCancelableRequest } from 'src/modules/shared/utils/api/cancelable-request.util';

interface UseCallsParams extends ApiQueryParams {
  /** Whether Calls data and imperative pagination are active. */
  enabled?: boolean;
}

interface UseCallsResponse extends ApiResponse {
  /** Accumulated Calls rows for the active query. */
  calls: Call_V2[];
  /** Loads the next cursor page when the query is enabled. */
  fetchNextPage: () => void;
  /** Whether the active query exposes another cursor page. */
  hasNextPage: boolean;
  /** Whether the active query is loading another cursor page. */
  isFetchingNextPage: boolean;
}

/**
 * Loads and accumulates cursor-paginated Calls.
 *
 * Disabled queries deliberately hide cached rows and guard imperative
 * pagination so invalid search text cannot expose or append unfiltered data.
 *
 * @param params - Generated Calls query parameters plus an enabled guard.
 * @returns Accumulated rows and guarded query/pagination state.
 */
export const useCalls = (params: UseCallsParams = {}): UseCallsResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchCalls = async ({
    pageParam,
    signal,
  }: {
    pageParam: string | undefined;
    signal: AbortSignal;
  }): Promise<CallListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    const request = V2CallsService.getCalls(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
    return await awaitCancelableRequest(request, signal);
  };

  const getNextPageParam = (lastPage: CallListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage: fetchNextPageQuery,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['calls', queryParams],
    queryFn: fetchCalls,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const calls = useMemoizedArrayData<Call_V2>(
    enabled ? data?.pages.flatMap((page) => page.items) ?? [] : []
  );

  return {
    loading: enabled && loading,
    error: enabled ? error : null,
    calls,
    fetchNextPage: () => {
      if (!enabled) {
        return;
      }
      void fetchNextPageQuery();
    },
    hasNextPage: enabled && hasNextPage,
    isFetchingNextPage: enabled && isFetchingNextPage,
    refetch,
  };
};

interface UseCallResponse extends ApiResponse {
  call: Call_V2 | null;
}

export const useCall = (id: number, enabled = true): UseCallResponse => {
  const fetchCall = async (): Promise<CallGetResponse> => {
    return await V2CallsService.getCall(id);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['calls', 'detail', id],
    queryFn: fetchCall,
    enabled,
  });

  return {
    loading,
    error,
    call: data ?? null,
    refetch,
  };
};
